import { create } from "zustand";
import {
  isBulkRowFinished,
  isBulkRunFinished,
  type BulkProgressEvent,
  type BulkRun,
  type BulkRunRow,
} from "../types/bulkImport.types";

/**
 * The bulk upload a person is watching: its totals, its row-by-row log and
 * the lines about the run itself.
 *
 * It is fed from two sides at once: live "bulk_import_progress" messages over
 * SignalR, and the REST reads (on load, and a poll while the run is going, in
 * case the socket dropped). Either can arrive late, twice or out of order, so
 * every update is merged, never just written: totals only go up, a finished
 * row is never reopened and a finished run never goes back to running.
 */
interface BulkImportState {
  run: BulkRun | null;
  /** The log, by sheet row number. */
  rows: Record<number, BulkRunRow>;
  /** Lines about the run as a whole: started, resumed, finished. */
  runLines: string[];

  /** What the server says now. Opening a different run replaces everything. */
  hydrate: (run: BulkRun, rows: BulkRunRow[]) => void;
  applyEvent: (event: BulkProgressEvent) => void;
  reset: () => void;
}

const EMPTY = { run: null, rows: {} as Record<number, BulkRunRow>, runLines: [] as string[] };

const mergeStatus = (current: number, incoming: number): number =>
  isBulkRunFinished(current) && !isBulkRunFinished(incoming) ? current : incoming;

const mergeRow = (current: BulkRunRow | undefined, incoming: BulkRunRow): BulkRunRow =>
  current && isBulkRowFinished(current.status) && !isBulkRowFinished(incoming.status)
    ? current
    : { ...current, ...incoming };

export const useBulkImportStore = create<BulkImportState>((set) => ({
  ...EMPTY,

  hydrate: (run, rows) =>
    set((s) => {
      const sameRun = s.run?.id === run.id;
      const merged: Record<number, BulkRunRow> = sameRun ? { ...s.rows } : {};
      for (const row of rows) merged[row.rowNumber] = mergeRow(merged[row.rowNumber], row);

      if (!sameRun || !s.run) return { run, rows: merged, runLines: [] };

      return {
        run: {
          ...run,
          status: mergeStatus(s.run.status, run.status),
          processedRows: Math.max(s.run.processedRows, run.processedRows),
          succeededRows: Math.max(s.run.succeededRows, run.succeededRows),
          failedRows: Math.max(s.run.failedRows, run.failedRows),
        },
        rows: merged,
        runLines: s.runLines,
      };
    }),

  applyEvent: (event) =>
    set((s) => {
      // Progress for a run nobody has open here (another tab started it, or
      // the page has moved on) is not this screen's to show.
      if (!s.run || s.run.id !== event.runId) return s;

      const run: BulkRun = {
        ...s.run,
        status: mergeStatus(s.run.status, event.runStatus),
        totalRows: event.total || s.run.totalRows,
        processedRows: Math.max(s.run.processedRows, event.processed),
        succeededRows: Math.max(s.run.succeededRows, event.succeeded),
        failedRows: Math.max(s.run.failedRows, event.failed),
      };

      if (event.rowNumber == null || event.rowStatus == null) {
        const line = event.message?.trim();
        const repeated = !line || s.runLines[s.runLines.length - 1] === line;
        return {
          // A line about the run is its latest word on itself: "finished: 4 created, 1 failed".
          run: line ? { ...run, lastMessage: line } : run,
          runLines: repeated ? s.runLines : [...s.runLines, line],
        };
      }

      return {
        run,
        rows: {
          ...s.rows,
          [event.rowNumber]: mergeRow(s.rows[event.rowNumber], {
            rowNumber: event.rowNumber,
            status: event.rowStatus,
            message: event.message,
            contractId: event.contractId,
            contractNumber: event.contractNumber,
          }),
        },
      };
    }),

  reset: () => set({ ...EMPTY, rows: {}, runLines: [] }),
}));

/** The log in sheet order. */
export const selectBulkLog = (state: Pick<BulkImportState, "rows">): BulkRunRow[] =>
  Object.values(state.rows).sort((a, b) => a.rowNumber - b.rowNumber);
